import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../../services/api';
import { ArrowLeft, Plus, Mail, UserX, RefreshCw, Edit } from 'lucide-react';
import '../../../styles/admin/ERPEmployees.css';

const ERPEmployees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    hourly_rate: '',
    emergency_contact: '',
    allergies: '',
    notes: '',
    role: 'employee'
  });

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/api/employees');
      setEmployees(response.data.employees || []);
    } catch (error) {
      console.error('Erreur chargement employés:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingEmployee) {
        await api.put(`/api/employees/${editingEmployee.id}`, formData);
        alert('✅ Employé modifié avec succès');
      } else {
        const response = await api.post('/api/employees', formData);
        if (response.data.email_sent) {
          alert('✅ Employé créé et invitation envoyée par email!');
        } else {
          alert('⚠️ Employé créé mais email non envoyé. Vérifiez la configuration email.');
        }
      }
      
      setShowModal(false);
      setEditingEmployee(null);
      resetForm();
      fetchEmployees();
    } catch (error) {
      alert(`❌ Erreur: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      first_name: employee.first_name,
      last_name: employee.last_name,
      phone: employee.phone,
      email: employee.email,
      hourly_rate: employee.hourly_rate,
      emergency_contact: employee.emergency_contact || '',
      allergies: employee.allergies || '',
      role: employee.role
    });
    setShowModal(true);
  };

  const handleDeactivate = async (employeeId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir désactiver cet employé?')) return;
    
    try {
      await api.delete(`/api/employees/${employeeId}`);
      alert('✅ Employé désactivé');
      fetchEmployees();
    } catch (error) {
      alert(`❌ Erreur: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleResendInvitation = async (employeeId) => {
    try {
      const response = await api.post(`/api/employees/${employeeId}/resend-invitation`);
      if (response.data.email_sent) {
        alert('✅ Invitation renvoyée avec succès!');
      } else {
        alert('⚠️ Erreur lors de l\'envoi de l\'email');
      }
    } catch (error) {
      alert(`❌ Erreur: ${error.response?.data?.detail || error.message}`);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      hourly_rate: '',
      emergency_contact: '',
      allergies: '',
      role: 'employee'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      invited: { label: 'Invité', className: 'status-invited' },
      active: { label: 'Actif', className: 'status-active' },
      inactive: { label: 'Inactif', className: 'status-inactive' }
    };
    const badge = badges[status] || badges.invited;
    return <span className={`status-badge ${badge.className}`}>{badge.label}</span>;
  };

  if (loading) {
    return <div className="loading">Chargement des employés...</div>;
  }

  return (
    <div className="erp-employees-page">
      <div className="page-header">
        <div className="header-left">
          <Link to="/admin/erp" className="back-button">
            <ArrowLeft size={20} />
            Retour à l'ERP
          </Link>
          <h1>Gestion des Employés</h1>
        </div>
        <button className="btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
          <Plus size={20} />
          Nouvel employé
        </button>
      </div>

      <div className="employees-table-container">
        <table className="employees-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Email</th>
              <th>Téléphone</th>
              <th>Rôle</th>
              <th>Taux horaire</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {employees.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>
                  Aucun employé. Créez-en un pour commencer.
                </td>
              </tr>
            ) : (
              employees.map(emp => (
                <tr key={emp.id}>
                  <td>
                    <strong>{emp.first_name} {emp.last_name}</strong>
                  </td>
                  <td>{emp.email}</td>
                  <td>{emp.phone}</td>
                  <td>{emp.role}</td>
                  <td>{emp.hourly_rate.toFixed(2)} $/h</td>
                  <td>{getStatusBadge(emp.status)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon edit"
                        onClick={() => handleEdit(emp)}
                        title="Modifier"
                      >
                        <Edit size={16} />
                      </button>
                      
                      {emp.status === 'invited' && (
                        <button
                          className="btn-icon resend"
                          onClick={() => handleResendInvitation(emp.id)}
                          title="Renvoyer l'invitation"
                        >
                          <RefreshCw size={16} />
                        </button>
                      )}
                      
                      <button
                        className="btn-icon deactivate"
                        onClick={() => handleDeactivate(emp.id)}
                        title="Désactiver"
                      >
                        <UserX size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Création/Modification */}
      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); setEditingEmployee(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingEmployee ? 'Modifier l\'employé' : 'Nouvel employé'}</h2>
            
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Prénom *</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Nom *</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    disabled={editingEmployee !== null}
                  />
                  {editingEmployee && (
                    <small>L'email ne peut pas être modifié</small>
                  )}
                </div>
                
                <div className="form-group">
                  <label>Téléphone *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="819-555-1234"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Taux horaire ($/h) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Rôle *</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                    required
                  >
                    <option value="employee">Employé</option>
                    <option value="team_lead">Chef d'équipe</option>
                    <option value="manager">Gestionnaire</option>
                    <option value="admin">Administrateur</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Contact d'urgence</label>
                <input
                  type="text"
                  value={formData.emergency_contact}
                  onChange={(e) => setFormData({...formData, emergency_contact: e.target.value})}
                  placeholder="Nom et téléphone"
                />
              </div>

              <div className="form-group">
                <label>Allergies</label>
                <textarea
                  value={formData.allergies}
                  onChange={(e) => setFormData({...formData, allergies: e.target.value})}
                  placeholder="Allergies connues..."
                  rows="2"
                />
              </div>

              <div className="form-group">
                <label>Notes / Compétences</label>
                <textarea
                  value={formData.notes || ''}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Ex: fertilisation, aération, tonte, certifications..."
                  rows="3"
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => { setShowModal(false); setEditingEmployee(null); }}
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editingEmployee ? 'Modifier' : 'Créer et envoyer invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ERPEmployees;
